import { FC, PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react'
import Head from 'next/head'
import { useTranslation } from 'react-i18next'
import Script from 'next/script'
import { useRouter } from 'next/router'
import { useSelector } from 'react-redux'
import { selectedMetaData } from '@/redux/meta/selectors'
import { IMeta } from './meta.interface'
import { useQuery } from 'react-query'
import SeoService from '@/services/seo.service'
import OrganizationSchema from './OrganizationSchema'
import WebSiteSchema from './WebSiteSchema'
import LocalBusinessSchema from './LocalBusinessSchema'

const Meta: FC<PropsWithChildren<IMeta>> = ({
  title: propsTitle,
  description: propsDescription,
  keywords: propsKeywords,
  canonical: propsCanonical,
}) => {
  const { i18n } = useTranslation()
  const isRuPage = i18n.language === 'ru'
  const [showAfterScroll, setShowAfterScroll] = useState(false)
  const { pathname, query, asPath } = useRouter()
  const { data: sessionData } = useQuery(
    ['seo-session', 'session'],
    () => SeoService.getSeoSession(),
    {
      keepPreviousData: true,
      staleTime: 1000 * 60 * 60 * 24,
    }
  )

  const {
    title: contextTitle,
    description: contextDescription,
    keywords: contextKeywords,
    socialImage: contextImage,
    canonical: contextCanonical
  } = useSelector(selectedMetaData)

  const title = propsTitle || contextTitle || ''
  const description = propsDescription || contextDescription || ''
  const keywords = propsKeywords || contextKeywords || ''

  const canonical = propsCanonical || contextCanonical || asPath.split('?')[0].split('#')[0] || ''

  const image =
    contextImage || 'https://www.majordomo.ru/img/adds/banners/new/1200x630/1200x630.jpg'

  const handleScroll = useCallback(() => {
    const show = window.scrollY > 200
    setShowAfterScroll((prev) => (prev !== show ? show : prev))
  }, [])

  useEffect(() => {
    document.addEventListener('scroll', handleScroll)
    return () => {
      document.removeEventListener('scroll', handleScroll)
    }
  }, [handleScroll])

  useEffect(() => {
    try {
      // @ts-ignore
      ym(process.env.NEXT_PUBLIC_YM_ID, 'hit', window.location.href)
    } catch (e) {
      console.error(e)
    }
  }, [pathname, query])

  useEffect(() => {
    if (sessionData) {
      try {
        // @ts-ignore
        ym(process.env.NEXT_PUBLIC_YM_ID, 'userParams', {
          ip: atob(sessionData.visitor_identifier),
        })
      } catch (error) {
        console.error(error)
      }
    }
  }, [sessionData])

  const canonicalUrl = useMemo(
    () => 
      `https://${process.env.NEXT_PUBLIC_DOMAIN}${canonical}`, 
    [asPath]
  )

  const ogType = useMemo(() => {
    const articlePaths = ['/articles/', '/news/', '/glossary/', '/help/instructions/']
    return articlePaths.some(path => asPath.includes(path)) ? 'article' : 'website'
  }, [asPath])

  return (
    <>
      <Head>
        <title>{title}</title>
        {description && isRuPage && <meta name="description" content={description} />}
        {keywords && <meta name="keywords" content={keywords} />}
        {canonicalUrl && (
          <link rel="canonical" href={canonicalUrl} />
        )}
        <link rel="icon" href="/favicon.ico" type="image/x-icon" />
        <link rel="icon" href="/favicon.svg" type="image/svg+xml" />
        <meta name="yandex-verification" content="6db4eb4c7452ea06" />
        <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0, user-scalable=yes"  />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@majordomo_live" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={image} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={image} />
        <meta property="og:site_name" content="Majordomo - Хостинг-провайдер" />
        <meta property="og:locale" content="ru_RU" />
        <meta property="og:type" content="website" />
        <meta name="theme-color" content="#eb6e00" />
        <meta name="theme-color" media="(prefers-color-scheme: dark)" content="#001A33" />
        {!isRuPage && <meta name="robots" content="none" />}
      </Head>
      <div
        dangerouslySetInnerHTML={{
          __html: `<script type="text/javascript" defer> (function(m,e,t,r,i,k,a){m[i]=m[i]||function(){(m[i].a=m[i].a||[]).push(arguments)}; m[i].l=1*new Date(); for (var j = 0; j < document.scripts.length; j++) {if (document.scripts[j].src === r) { return; }} k=e.createElement(t),a=e.getElementsByTagName(t)[0],k.async=1,k.src=r,a.parentNode.insertBefore(k,a)}) (window, document, "script", "https://mc.yandex.ru/metrika/tag.js", "ym"); ym(${process.env.NEXT_PUBLIC_YM_ID}, "init", { clickmap:true, trackLinks:true, accurateTrackBounce:true, webvisor:true, ecommerce:"dataLayer" }); </script> <noscript><div><img src="https://mc.yandex.ru/watch/${process.env.NEXT_PUBLIC_YM_ID}" style="position:absolute; left:-9999px;" alt="" /></div></noscript>`,
        }}
      />
      <div
        dangerouslySetInnerHTML={{
          __html: `<script type=\"text/javascript\" defer> (function(e, x, pe, r, i, me, nt){ e[i]=e[i]||function(){(e[i].a=e[i].a||[]).push(arguments)}, me=x.createElement(pe),me.async=1,me.src=r,nt=x.getElementsByTagName(pe)[0],me.addEventListener('error',function(){function cb(t){t=t[t.length-1],'function'==typeof t&&t({flags:{}})};Array.isArray(e[i].a)&&e[i].a.forEach(cb);e[i]=function(){cb(arguments)}}),nt.parentNode.insertBefore(me,nt)}) (window, document, 'script', 'https://abt.s3.yandex.net/expjs/latest/exp.js', 'ymab'); ymab('metrika.${process.env.NEXT_PUBLIC_YM_ID}', 'init'/*, {clientFeatures}, {callback}*/); </script>`,
        }}
      />
      {showAfterScroll && (
        <>
          <Script defer src="//code.jivosite.com/script/widget/wFI8uUtpEz" strategy="lazyOnload" />
          <Script defer id="topmailru-code" src="//top-fwz1.mail.ru/js/code.js" strategy="lazyOnload" />
          <Script defer src="//mc.yandex.ru/metrika/tag.js" strategy="lazyOnload" />
          <Script defer src="//vk.com/js/api/openapi.js?169" strategy="lazyOnload" />
        </>
      )}
      <OrganizationSchema />
      {ogType === 'website' && <WebSiteSchema />}
      <LocalBusinessSchema />
    </>
  )
}

export default Meta
